import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useConnection } from '../connection';
import { useWallet } from '../wallet';
import { AccountInfo, ParsedAccountData, PublicKey } from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  USDT_PROGRAM_ID,
  WUSDT_MINT,
} from './instructions';
import { getMetadataPDA, deserializeMetadataData } from './metadata';

export type TokenAccount = {
  pubkey: PublicKey;
  account: AccountInfo<ParsedAccountData>;
};

export function useTokenAccounts(programId: PublicKey) {
  const wallet = useWallet();
  const connection = useConnection();

  return useQuery<TokenAccount[]>({
    queryKey: [
      'tokenAccounts',
      wallet.publicKey,
      connection.rpcEndpoint,
      programId,
    ],
    queryFn: async () => {
      const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
        wallet.publicKey,
        { programId },
      );
      return tokenAccounts.value;
    },
    refetchInterval: 30 * 1000,
    meta: {
      persist: true,
    },
  });
}

// Owning token program of a parsed token account. Legacy USDT accounts
// resolve to USDT_PROGRAM_ID; wUSDT accounts resolve to TOKEN_PROGRAM_ID.
export function tokenAccountProgramId(account: TokenAccount): PublicKey {
  return account.account.owner;
}

// USDT exists as two separate wallet items. 'legacy' spends the per-deposit
// mints under the USDT program; 'wusdt' spends the singleton wUSDT mint
// under the main token program. Scopes never mix in one transfer.
export type UsdtScope = 'legacy' | 'wusdt';

export function useUsdtAccounts(scope: UsdtScope) {
  const legacy = useTokenAccounts(USDT_PROGRAM_ID);
  const main = useTokenAccounts(TOKEN_PROGRAM_ID);

  const data = useMemo<TokenAccount[] | undefined>(() => {
    if (scope === 'legacy') {
      return legacy.data;
    }
    if (!main.data) {
      return undefined;
    }
    const wusdtMintAddress = WUSDT_MINT?.toBase58();
    return wusdtMintAddress
      ? main.data.filter(
          (account) =>
            account.account.data.parsed.info.mint === wusdtMintAddress,
        )
      : [];
  }, [scope, legacy.data, main.data]);

  return {
    data,
    isLoading: scope === 'legacy' ? legacy.isLoading : main.isLoading,
    isError: scope === 'legacy' ? legacy.isError : main.isError,
  };
}


export function useTokenLogoUris(mintAddresses: string[]) {
  const connection = useConnection();
  const key = Array.from(new Set(mintAddresses.filter(Boolean)))
    .sort()
    .join(',');

  return useQuery<Record<string, string>>({
    queryKey: ['tokenLogoUris', connection.rpcEndpoint, key],
    enabled: key.length > 0,
    queryFn: async () => {
      const mints = key.split(',').map((address) => new PublicKey(address));
      const accounts = await connection.getMultipleAccountsInfo(
        mints.map((mint) => getMetadataPDA(mint)),
      );
      const uris: Record<string, string> = {};
      accounts.forEach((account, index) => {
        if (!account) {
          return;
        }
        const data = deserializeMetadataData(account.data);
        const uri = (data?.uri ?? '').replace(
          /^[\s\uFEFF\xA0\0]+|[\s\uFEFF\xA0\0]+$/g,
          '',
        );
        if (uri) {
          uris[mints[index].toBase58()] = uri;
        }
      });
      return uris;
    },
    meta: {
      persist: true,
    },
  });
}
